import React, { useEffect, useState } from 'react';
import { Button, Form, Label, Pagination, Popup, Table, Dropdown, Grid, Segment, Modal } from 'semantic-ui-react';
import { Link } from 'react-router-dom';
import { API, showError, showSuccess } from '../helpers';

import { ITEMS_PER_PAGE } from '../constants';
import { renderGroup, renderNumber, renderQuota, renderText } from '../helpers/render';

// Lazy-loaded action quota popup: fetches data only when the popup opens.
const ActionQuotaPopup = ({ userId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleOpen = async () => {
    if (data) return; // already loaded
    setLoading(true);
    try {
      const res = await API.get(`/api/user/${userId}/actions`);
      setData(res.data);
    } catch (_) {
      setData(null);
    }
    setLoading(false);
  };

  const planLabels = {
    FREE: '免费', ONE_TIME: '一次性',
    BASIC_MONTHLY: '基础月付', BASIC_YEARLY: '基础年付',
    PLUS_MONTHLY: '高级月付', PLUS_YEARLY: '高级年付',
  };

  const content = loading ? '加载中…' : data ? (
    <div style={{ minWidth: 180 }}>
      <div><strong>Action 计划:</strong> {planLabels[data.subscription_plan] || data.subscription_plan || '-'}</div>
      <div><strong>总 Actions:</strong> {data.total_actions ?? '-'}</div>
      <div><strong>已用:</strong> {data.used_actions ?? '-'}</div>
      <div><strong>剩余:</strong> {(data.total_actions ?? 0) - (data.used_actions ?? 0)}</div>
      {data.daily_limit > 0 ? <>
        <div><strong>每日限额:</strong> {data.daily_limit}</div>
        <div><strong>今日已用:</strong> {data.daily_used}</div>
        <div><strong>今日剩余:</strong> {data.daily_limit - (data.daily_used ?? 0)}</div>
      </> : <div><strong>每日限额:</strong> 无限制</div>}
      <div style={{ marginTop: 6 }}>
        <a href={`/user/edit/${userId}`} target='_blank' rel='noopener noreferrer'>编辑配额 →</a>
      </div>
    </div>
  ) : '暂无 Action 数据';

  return (
    <Popup
      content={content}
      on='click'
      onOpen={handleOpen}
      trigger={<Label basic color='teal' style={{ cursor: 'pointer', marginLeft: 2 }}>Actions</Label>}
    />
  );
};

function renderRole(role) {
  switch (role) {
    case 1:
      return <Label>普通用户</Label>;
    case 10:
      return <Label color='yellow'>管理员</Label>;
    case 100:
      return <Label color='orange'>超级管理员</Label>;
    default:
      return <Label color='red'>未知身份</Label>;
  }
}

function renderRegistrationSource(source) {
  switch (source) {
    case 'direct':
      return <Label color='blue'>自主注册</Label>;
    case 'affiliate':
      return <Label color='green'>推荐注册</Label>;
    case 'invite_code':
      return <Label color='purple'>邀请码注册</Label>;
    default:
      return <Label color='grey'>未知来源</Label>;
  }
}

const UsersTable = () => {
  const [users, setUsers] = useState([]);
  // 删除确认弹窗相关状态
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activePage, setActivePage] = useState(1);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [searching, setSearching] = useState(false);
  const [orderBy, setOrderBy] = useState('');
  
  // 筛选状态
  const [filters, setFilters] = useState({
    role: '',
    status: '',
    registrationSource: '',
    group: '',
    inviteCodeId: ''
  });
  const [inviteCodes, setInviteCodes] = useState([]);

  const loadInviteCodes = async () => {
    try {
      const res = await API.get('/api/admin/invite-codes?size=1000');
      if (res.data.success) {
        setInviteCodes(res.data.data || []);
      }
    } catch (error) {
      console.error('加载邀请码列表失败:', error);
    }
  };

  const loadUsers = async (startIdx) => {
    // 构建查询参数
    const params = new URLSearchParams({
      p: startIdx,
      order: orderBy
    });
    
    // 添加筛选参数
    Object.keys(filters).forEach(key => {
      if (filters[key]) {
        // 将驼峰命名转换为下划线命名以匹配后端API
        const apiKey = key === 'registrationSource' ? 'registration_source' : 
                      key === 'inviteCodeId' ? 'invite_code_id' : key;
        params.append(apiKey, filters[key]);
      }
    });
    
    try {
      const res = await API.get(`/api/user/?${params.toString()}`);
      const { success, message, data } = res.data;
      if (success) {
        if (startIdx === 0) {
          setUsers(data || []);
        } else {
          let newUsers = users || [];
          if (data && Array.isArray(data)) {
            newUsers.push(...data);
          }
          setUsers(newUsers);
        }
      } else {
        showError(message);
        // 如果API调用失败，重置users为空数组
        if (startIdx === 0) {
          setUsers([]);
        }
      }
    } catch (error) {
      showError('加载用户数据失败: ' + error.message);
      // 如果发生错误，重置users为空数组
      if (startIdx === 0) {
        setUsers([]);
      }
    }
    setLoading(false);
  };

  const onPaginationChange = (e, { activePage }) => {
    (async () => {
      const usersLength = (users || []).length;
      if (activePage === Math.ceil(usersLength / ITEMS_PER_PAGE) + 1) {
        // In this case we have to load more data and then append them.
        await loadUsers(activePage - 1, orderBy);
      }
      setActivePage(activePage);
    })();
  };

  useEffect(() => {
    loadUsers(0, orderBy)
      .then()
      .catch((reason) => {
        showError(reason);
      });
  }, [orderBy, filters]);

  useEffect(() => {
    loadInviteCodes();
  }, []);

  // 删除用户逻辑
  const manageUser = (username, action, idx) => {
    (async () => {
      const res = await API.post('/api/user/manage', {
        username,
        action
      });
      const { success, message } = res.data;
      if (success) {
        showSuccess('操作成功完成！');
        if (action === 'delete') {
          // 删除后自动刷新用户列表
          await loadUsers(0, orderBy);
        } else {
          let user = res.data.data;
          let newUsers = [...users];
          let realIdx = (activePage - 1) * ITEMS_PER_PAGE + idx;
          newUsers[realIdx].status = user.status;
          newUsers[realIdx].role = user.role;
          setUsers(newUsers);
        }
      } else {
        showError(message);
      }
      // 删除后关闭弹窗
      setDeleteModalOpen(false);
      setUserToDelete(null);
    })();
  };

  const renderStatus = (status) => {
    switch (status) {
      case 1:
        return <Label basic>已激活</Label>;
      case 2:
        return (
          <Label basic color='red'>
            已封禁
          </Label>
        );
      default:
        return (
          <Label basic color='grey'>
            未知状态
          </Label>
        );
    }
  };

  const searchUsers = async () => {
    if (searchKeyword === '') {
      // if keyword is blank, load files instead.
      await loadUsers(0);
      setActivePage(1);
      setOrderBy('');
      return;
    }
    setSearching(true);
    const res = await API.get(`/api/user/search?keyword=${searchKeyword}`);
    const { success, message, data } = res.data;
    if (success) {
      setUsers(data);
      setActivePage(1);
    } else {
      showError(message);
    }
    setSearching(false);
  };

  const handleKeywordChange = async (e, { value }) => {
    setSearchKeyword(value.trim());
  };

  const sortUser = (key) => {
    const usersArray = users || [];
    if (usersArray.length === 0) return;
    setLoading(true);
    let sortedUsers = [...usersArray];
    sortedUsers.sort((a, b) => {
      if (!isNaN(a[key])) {
        // If the value is numeric, subtract to sort
        return a[key] - b[key];
      } else {
        // If the value is not numeric, sort as strings
        return ('' + a[key]).localeCompare(b[key]);
      }
    });
    if (sortedUsers[0].id === usersArray[0].id) {
      sortedUsers.reverse();
    }
    setUsers(sortedUsers);
    setLoading(false);
  };

  const handleOrderByChange = (e, { value }) => {
    setOrderBy(value);
    setActivePage(1);
  };

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
    setActivePage(1);
  };

  const clearFilters = () => {
    setFilters({
      role: '',
      status: '',
      registrationSource: '',
      group: '',
      inviteCodeId: ''
    });
    setActivePage(1);
  };

  return (
    <>
      <Form onSubmit={searchUsers}>
        <Form.Input
          icon='search'
          fluid
          iconPosition='left'
          placeholder='搜索用户的 ID，用户名，显示名称，以及邮箱地址 ...'
          value={searchKeyword}
          loading={searching}
          onChange={handleKeywordChange}
        />
      </Form>

      {/* 筛选器 */}
      <Segment>
        <Grid>
          <Grid.Row>
            <Grid.Column width={3}>
              <Dropdown
                placeholder='角色筛选'
                selection
                clearable
                options={[
                  { key: 1, text: '普通用户', value: 1 },
                  { key: 10, text: '管理员', value: 10 },
                  { key: 100, text: '超级管理员', value: 100 }
                ]}
                value={filters.role}
                onChange={(e, { value }) => handleFilterChange('role', value)}
              />
            </Grid.Column>
            <Grid.Column width={3}>
              <Dropdown
                placeholder='状态筛选'
                selection
                clearable
                options={[
                  { key: 1, text: '已激活', value: 1 },
                  { key: 2, text: '已封禁', value: 2 }
                ]}
                value={filters.status}
                onChange={(e, { value }) => handleFilterChange('status', value)}
              />
            </Grid.Column>
            <Grid.Column width={3}>
              <Dropdown
                placeholder='注册来源筛选'
                selection
                clearable
                options={[
                  { key: 'direct', text: '自主注册', value: 'direct' },
                  { key: 'affiliate', text: '推荐注册', value: 'affiliate' },
                  { key: 'invite_code', text: '邀请码注册', value: 'invite_code' }
                ]}
                value={filters.registrationSource}
                onChange={(e, { value }) => handleFilterChange('registrationSource', value)}
              />
            </Grid.Column>
            <Grid.Column width={3}>
              <Form.Input
                placeholder='分组筛选'
                value={filters.group}
                onChange={(e, { value }) => handleFilterChange('group', value)}
              />
            </Grid.Column>
            <Grid.Column width={3}>
              <Dropdown
                placeholder='邀请码筛选'
                selection
                clearable
                search
                options={inviteCodes.map(code => ({
                  key: code.id,
                  text: `${code.code} (${code.name || code.code})`,
                  value: code.id
                }))}
                value={filters.inviteCodeId}
                onChange={(e, { value }) => handleFilterChange('inviteCodeId', value)}
              />
            </Grid.Column>
            <Grid.Column width={4}>
              <Button onClick={clearFilters} basic>
                清除筛选
              </Button>
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </Segment>

      <Table basic compact size='small'>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell
              style={{ cursor: 'pointer' }}
              onClick={() => {
                sortUser('id');
              }}
            >
              ID
            </Table.HeaderCell>
            <Table.HeaderCell
              style={{ cursor: 'pointer' }}
              onClick={() => {
                sortUser('username');
              }}
            >
              用户名
            </Table.HeaderCell>
            <Table.HeaderCell
              style={{ cursor: 'pointer' }}
              onClick={() => {
                sortUser('group');
              }}
            >
              分组
            </Table.HeaderCell>
            <Table.HeaderCell
              style={{ cursor: 'pointer' }}
              onClick={() => {
                sortUser('quota');
              }}
            >
              统计信息
            </Table.HeaderCell>
            <Table.HeaderCell
              style={{ cursor: 'pointer' }}
              onClick={() => {
                sortUser('role');
              }}
            >
              用户角色
            </Table.HeaderCell>
            <Table.HeaderCell
              style={{ cursor: 'pointer' }}
              onClick={() => {
                sortUser('status');
              }}
            >
              状态
            </Table.HeaderCell>
            <Table.HeaderCell
              style={{ cursor: 'pointer' }}
              onClick={() => {
                sortUser('registration_source');
              }}
            >
              注册来源
            </Table.HeaderCell>
            <Table.HeaderCell
              style={{ cursor: 'pointer' }}
              onClick={() => {
                sortUser('project_count');
              }}
            >
              项目数量
            </Table.HeaderCell>
            <Table.HeaderCell>操作</Table.HeaderCell>
          </Table.Row>
        </Table.Header>

        <Table.Body>
          {(users || [])
            .slice(
              (activePage - 1) * ITEMS_PER_PAGE,
              activePage * ITEMS_PER_PAGE
            )
            .map((userData, idx) => {
              // 处理新的数据结构，支持UserWithProjectCount
              const user = userData.user || userData;
              const projectCount = userData.project_count || 0;
              
              if (user.deleted) return <></>;
              return (
                <Table.Row key={user.id}>
                  <Table.Cell>{user.id}</Table.Cell>
                  <Table.Cell>
                    <Popup
                      content={user.email ? user.email : '未绑定邮箱地址'}
                      key={user.username}
                      header={user.display_name ? user.display_name : user.username}
                      trigger={<span>{renderText(user.username, 15)}</span>}
                      hoverable
                    />
                  </Table.Cell>
                  <Table.Cell>{renderGroup(user.group)}</Table.Cell>
                  {/*<Table.Cell>*/}
                  {/*  {user.email ? <Popup hoverable content={user.email} trigger={<span>{renderText(user.email, 24)}</span>} /> : '无'}*/}
                  {/*</Table.Cell>*/}
                  <Table.Cell>
                    <Popup content='剩余额度' trigger={<Label basic>{renderQuota(user.quota)}</Label>} />
                    <Popup content='已用额度' trigger={<Label basic>{renderQuota(user.used_quota)}</Label>} />
                    <Popup content='请求次数' trigger={<Label basic>{renderNumber(user.request_count)}</Label>} />
                    <ActionQuotaPopup userId={user.id} />
                  </Table.Cell>
                  <Table.Cell>{renderRole(user.role)}</Table.Cell>
                  <Table.Cell>{renderStatus(user.status)}</Table.Cell>
                  <Table.Cell>{renderRegistrationSource(user.registration_source)}</Table.Cell>
                  <Table.Cell>
                    <Label basic color='teal'>{projectCount}</Label>
                  </Table.Cell>
                  <Table.Cell>
                    <div>
                      <Button
                        size={'small'}
                        positive
                        onClick={() => {
                          manageUser(user.username, 'promote', idx);
                        }}
                        disabled={user.role === 100}
                      >
                        提升
                      </Button>
                      <Button
                        size={'small'}
                        color={'yellow'}
                        onClick={() => {
                          manageUser(user.username, 'demote', idx);
                        }}
                        disabled={user.role === 100}
                      >
                        降级
                      </Button>
                      <Button
                        size='small'
                        negative
                        disabled={user.role === 100}
                        onClick={() => {
                          setUserToDelete({ username: user.username, idx, displayName: user.display_name || user.username });
                          setDeleteModalOpen(true);
                        }}
                      >
                        删除
                      </Button>
                      {/* 删除确认 Modal 对话框 */}
                      <Modal
                        open={deleteModalOpen}
                        size='small'
                        onClose={() => {
                          setDeleteModalOpen(false);
                          setUserToDelete(null);
                        }}
                      >
                        <Modal.Header>删除确认</Modal.Header>
                        <Modal.Content>
                          <p>确定要删除「{userToDelete?.displayName}」吗？删除后将无法恢复。</p>
                        </Modal.Content>
                        <Modal.Actions>
                          <Button onClick={() => {
                            setDeleteModalOpen(false);
                            setUserToDelete(null);
                          }}>
                            取消
                          </Button>
                          <Button
                            negative
                            onClick={() => {
                              if (userToDelete) {
                                manageUser(userToDelete.username, 'delete', userToDelete.idx);
                              }
                            }}
                          >
                            删除
                          </Button>
                        </Modal.Actions>
                      </Modal>
                      <Button
                        size={'small'}
                        onClick={() => {
                          manageUser(
                            user.username,
                            user.status === 1 ? 'disable' : 'enable',
                            idx
                          );
                        }}
                        disabled={user.role === 100}
                      >
                        {user.status === 1 ? '禁用' : '启用'}
                      </Button>
                      <Button
                        size={'small'}
                        as={Link}
                        to={'/user/edit/' + user.id}
                      >
                        编辑
                      </Button>
                      <Button
                        size={'small'}
                        color={'blue'}
                        as={Link}
                        to={'/user/' + user.id}
                      >
                        详情
                      </Button>
                    </div>
                  </Table.Cell>
                </Table.Row>
              );
            })}
        </Table.Body>

        <Table.Footer>
          <Table.Row>
            <Table.HeaderCell colSpan='9'>
              <Button size='small' as={Link} to='/user/add' loading={loading}>
                添加新的用户
              </Button>
              <Dropdown
                placeholder='排序方式'
                selection
                options={[
                  { key: '', text: '默认排序', value: '' },
                  { key: 'quota', text: '按剩余额度排序', value: 'quota' },
                  { key: 'used_quota', text: '按已用额度排序', value: 'used_quota' },
                  { key: 'request_count', text: '按请求次数排序', value: 'request_count' },
                  { key: 'created_at', text: '按创建时间排序', value: 'created_at' },
                ]}
                value={orderBy}
                onChange={handleOrderByChange}
                style={{ marginLeft: '10px' }}
              />
              <Pagination
                floated='right'
                activePage={activePage}
                onPageChange={onPaginationChange}
                size='small'
                siblingRange={1}
                totalPages={
                  Math.ceil((users || []).length / ITEMS_PER_PAGE) +
                  ((users || []).length % ITEMS_PER_PAGE === 0 ? 1 : 0)
                }
              />
            </Table.HeaderCell>
          </Table.Row>
        </Table.Footer>
      </Table>
    </>
  );
};

export default UsersTable;
